import { Outlet } from 'react-router';

import { accentColor, AUTH_ACCENT_HUE, AUTH_COPY, AUTH_FEATURES } from '@/components/auth/auth-config';
import { Brandmark } from '@/components/auth/auth-icons';
import { AuthThemeToggle } from '@/components/auth/auth-theme-toggle';
import { FeatureCard } from '@/components/auth/feature-card';
import { FlowBackground } from '@/components/auth/flow-background';
import { useTheme } from '@/context';
import { LegalFooter } from '@/legal';

/**
 * Four small L-shaped marks pinned to the corners of the left pane.
 * Pure decorative chrome from the source design.
 */
function CornerTicks() {
    const base = 'absolute h-[10px] w-[10px] border-input';
    return (
        <>
            <div className={`${base} top-[18px] left-[18px] border-t border-l`} aria-hidden="true" />
            <div className={`${base} top-[18px] right-[18px] border-t border-r`} aria-hidden="true" />
            <div className={`${base} bottom-[18px] left-[18px] border-b border-l`} aria-hidden="true" />
            <div className={`${base} bottom-[18px] right-[18px] border-b border-r`} aria-hidden="true" />
        </>
    );
}

/**
 * Two-pane auth shell:
 *   - LEFT: animated centerline canvas with brand, hero copy and three
 *     feature cards (hidden on small screens).
 *   - RIGHT: themed dotted-mask backdrop around the auth route Outlet
 *     (LoginPage, LogoutPage, …).
 *
 * Accent hue, copy and feature list come from
 * `components/auth/auth-config.ts`. Theme reactivity is automatic via
 * `useTheme()` + Tailwind `dark:` variants.
 */
export function AuthLayout() {
    const { resolvedTheme } = useTheme();

    return (
        <div
            className="relative flex h-screen w-screen bg-background"
            style={{
                // Expose the accent as a CSS variable so descendants can
                // pick it up via Tailwind arbitrary-value classes:
                //   text-[color:var(--auth-accent)]
                //   bg-[color:var(--auth-accent)]
                ['--auth-accent' as string]: accentColor(55, 0.18, undefined, AUTH_ACCENT_HUE),
            }}
        >
            {/* LEFT — hero & background (lg+) */}
            <div className="relative hidden min-w-0 flex-[1_1_62%] flex-col border-r border-border px-14 pt-10 pb-9 lg:flex">
                <FlowBackground accentHue={AUTH_ACCENT_HUE} resolvedTheme={resolvedTheme} />

                {/* Overlay content */}
                <div className="relative z-10 flex flex-1 flex-col">
                    {/* Top bar */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3" style={{ color: 'var(--auth-accent)' }}>
                            <Brandmark className="h-[30px] w-[30px]" />
                            <div className="flex flex-col leading-[1.05]">
                                <span className="text-[18px] font-semibold tracking-[-0.01em] text-foreground">
                                    ESCloud
                                </span>
                                <span className="font-mono text-[10px] tracking-[0.14em] text-muted-foreground/80">
                                    ENDOVASCULAR · PLANNING
                                </span>
                            </div>
                        </div>
                        <div className="flex gap-[22px] font-mono text-[10.5px] tracking-[0.14em] text-muted-foreground/80">
                            <span>PLATFORM</span>
                            <span>RESOURCES</span>
                            <span>CONTACT</span>
                        </div>
                    </div>

                    {/* Hero */}
                    <div className="mt-6 flex max-w-[720px] flex-1 flex-col justify-center">
                        <div
                            className="mb-[22px] flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.16em]"
                            style={{ color: 'var(--auth-accent)' }}
                        >
                            <span className="block h-px w-6" style={{ background: 'var(--auth-accent)' }} />
                            {AUTH_COPY.eyebrow}
                        </div>
                        <h1 className="mb-[22px] text-balance text-[clamp(36px,4.4vw,60px)] font-medium leading-[1.04] tracking-[-0.025em] text-foreground">
                            {AUTH_COPY.headline}
                        </h1>
                        <p className="max-w-[560px] text-pretty text-[17px] leading-[1.55] text-muted-foreground">
                            {AUTH_COPY.subheadline}
                        </p>
                    </div>

                    {/* Features */}
                    <div className="mt-7 flex gap-3.5">
                        {AUTH_FEATURES.map((feature) => (
                            <FeatureCard key={feature.code} feature={feature} accentHue={AUTH_ACCENT_HUE} />
                        ))}
                    </div>

                    {/* Footer */}
                    <div className="mt-7 flex items-center justify-between font-mono text-[10.5px] uppercase tracking-[0.12em] text-muted-foreground/80">
                        <span>ESCloud · v{__APP_VERSION__}</span>
                        <span>© 2026 Therenva</span>
                    </div>
                </div>

                <CornerTicks />
            </div>

            {/* RIGHT — sign-in / logout slot */}
            <div className="relative flex w-full flex-1 items-center justify-center bg-muted/40 p-6 sm:p-10 lg:w-auto lg:min-w-[440px] lg:flex-[0_0_38%]">
                {/* Dotted radial mask */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 [background-image:radial-gradient(rgba(15,23,35,0.08)_1px,transparent_1px)] [background-size:22px_22px] [mask-image:radial-gradient(ellipse_at_center,rgba(0,0,0,0.8)_0%,rgba(0,0,0,0)_70%)] [-webkit-mask-image:radial-gradient(ellipse_at_center,rgba(0,0,0,0.8)_0%,rgba(0,0,0,0)_70%)] dark:[background-image:radial-gradient(rgba(255,255,255,0.07)_1px,transparent_1px)]"
                />

                {/* Theme toggle pinned top-right */}
                <div className="absolute top-5 right-5 z-20">
                    <AuthThemeToggle />
                </div>

                {/* Mobile-only brand (visible when left pane is hidden) */}
                <div className="absolute top-5 left-5 z-20 flex items-center gap-2 lg:hidden">
                    <Brandmark className="h-6 w-6 text-[color:var(--auth-accent)]" />
                    <span className="text-sm font-semibold tracking-[-0.01em] text-foreground">ESCloud</span>
                </div>

                <Outlet />

                {/* Legal links pinned to the bottom of the pane (ESCLOUD-78) */}
                <div className="absolute inset-x-0 bottom-3 z-20">
                    <LegalFooter className="border-t-0 bg-transparent" />
                </div>
            </div>
        </div>
    );
}
